<template>
  <v-container fluid class="patient-bar">
    <h3 class="patient-bar__label">Identify Patient</h3>
    {{ void (patientValue = newPatient ?? editablePatient) }}
    <v-form v-if="isChangingPatient && !addPatientState" :rules="[patientRequiredRule]" @submit.prevent>
      <v-row dense>
        <v-col v-if="patientRequiredRule" cols="12">
          <span class="text-error">
            <v-icon>mdi-information-outline</v-icon> Search for a patient using the following fields or create a new one
          </span>
        </v-col>
        <v-col cols="6" sm="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(PATIENT_IDENTITY_ROW_FIELDS)"
            :metadata="guessMetaData('firstName')"
            label="First Name"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-text-field
            v-model="searchablePatient.firstName"
            :disabled="!props.editable"
            type="text"
            label="First Name" />
        </v-col>
        <v-col cols="6" sm="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(PATIENT_IDENTITY_ROW_FIELDS)"
            :metadata="guessMetaData('lastName')"
            label="Last Name"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-text-field
            v-model="searchablePatient.lastName"
            :disabled="!props.editable"
            type="text"
            label="Last Name" />
        </v-col>
        <v-col cols="6" sm="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(PATIENT_IDENTITY_ROW_FIELDS)"
            :metadata="guessMetaData('phoneNumber')"
            label="Phone Number"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-text-field
            v-model="searchablePatient.phoneNumber"
            :disabled="!props.editable"
            type="text"
            label="Phone Number" />
        </v-col>
        <v-col cols="6" sm="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(PATIENT_IDENTITY_ROW_FIELDS)"
            :metadata="guessMetaData('dob')"
            label="Date of Birth"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <text-date-input v-model="searchablePatient.dob" :disabled="!props.editable" label="Date of Birth" />
        </v-col>
        <v-col cols="12" sm="4">
          <GuessMetadataHeader
            v-if="rowGuessMeta(['address'])"
            :metadata="guessMetaData('address')"
            label="Address"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-text-field v-model="searchablePatient.address" :disabled="!props.editable" type="text" label="Address" />
        </v-col>
        <v-col cols="6" sm="3">
          <GuessMetadataHeader v-if="rowGuessMeta(['address'])" />
          <v-text-field
            v-model="searchablePatient.externalId"
            :disabled="!props.editable"
            type="text"
            label="Patient ID (MRN)"></v-text-field>
        </v-col>
      </v-row>
      <v-row v-if="props.editable">
        <v-col cols="auto">
          <v-btn color="primary" variant="flat" rounded prepend-icon="mdi-magnify" @click="searchPatient">
            Search Patient
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn
            color="primary"
            variant="tonal"
            rounded
            prepend-icon="mdi-account-plus-outline"
            @click="setAddPatientState">
            Register New Patient
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn v-if="patient" variant="outlined" color="primary" rounded @click="cancelChangePatient">Cancel</v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-dialog v-model="selectPatientDialog" max-width="900">
      <v-card>
        <v-card-title class="text-h6">Select a Patient</v-card-title>
        <v-card-text>
          <v-progress-linear v-if="searchLoading" indeterminate class="mb-4" />
          <template v-else>
            <div v-if="!searchResults || !searchResults.length" class="text-medium-emphasis">No patients found</div>
            <v-list v-else lines="two" density="comfortable">
              <v-list-item v-for="p in searchResults" :key="p.id" @click="onSelectPatient(p.id)">
                <v-list-item-title> {{ p.firstName }} {{ p.lastName }} </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="p.dob">{{ convertDateToMMDDYYYY(p.dob) }}</span>
                  <span v-else> DOB: N/A </span>
                  <span v-if="p.phoneNumber"> ••• {{ p.phoneNumber }}</span>
                  <span v-else> ••• PHONE: N/A </span>
                  <span v-if="p.address"> ••• {{ p.address }}</span>
                  <span v-if="p.externalId"> ••• Patient ID (MRN): {{ p.externalId }}</span>
                </v-list-item-subtitle>
                <template #append>
                  <v-btn-lh size="small" variant="text" @click.stop="onSelectPatient(p.id)">Select</v-btn-lh>
                </template>
              </v-list-item>
            </v-list>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn-lh variant="text" @click="selectPatientDialog = false">Close</v-btn-lh>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-form v-if="!isChangingPatient" ref="patientFormRef" action="" validate-on="eager" class="patient-bar-form">
      <v-row dense>
        <v-col cols="6" sm="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(PATIENT_IDENTITY_ROW_FIELDS)"
            :metadata="guessMetaData('firstName')"
            label="First Name"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-text-field
            type="text"
            :rules="patientNameRules"
            :model-value="patientValue?.firstName"
            :disabled="!props.editable"
            label="First Name"
            @update:model-value="(v) => onChangeInfo('firstName', v)" />
        </v-col>
        <v-col cols="6" sm="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(PATIENT_IDENTITY_ROW_FIELDS)"
            :metadata="guessMetaData('lastName')"
            label="Last Name"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-text-field
            type="text"
            :rules="patientNameRules"
            :model-value="patientValue?.lastName"
            :disabled="!props.editable"
            label="Last Name"
            @update:model-value="(v) => onChangeInfo('lastName', v)" />
        </v-col>
        <v-col cols="6" sm="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(PATIENT_IDENTITY_ROW_FIELDS)"
            :metadata="guessMetaData('phoneNumber')"
            label="Phone Number"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-text-field
            type="text"
            :model-value="patientValue?.phoneNumber"
            :disabled="!props.editable"
            :rules="patientPhoneRequired ? [requiredRule, patientPhoneLenghtRule] : [patientPhoneLenghtRule]"
            label="Phone Number"
            @update:model-value="(v) => onChangeInfo('phoneNumber', v)" />
        </v-col>
        <v-col cols="6" sm="3">
          <GuessMetadataHeader
            v-if="rowGuessMeta(PATIENT_IDENTITY_ROW_FIELDS)"
            :metadata="guessMetaData('dob')"
            label="Date of Birth"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <text-date-input
            :model-value="patientValue?.dob"
            :rules="patientDobRequired ? [requiredRule] : []"
            :disabled="!props.editable"
            label="Date of Birth"
            @update:model-value="(value) => onChangeInfo('dob', value)" />
        </v-col>
        <v-col v-if="patientAddress && !addPatientState" cols="12">
          <GuessMetadataHeader
            v-if="guessMetaData('address')"
            :metadata="guessMetaData('address')"
            label="Address"
            @highlight-bounding-box="onHighlightBoundingBox" />
          <v-textarea :model-value="patientAddress" :disabled="true" label="Address" rows="1" auto-grow no-resize />
        </v-col>
        <v-expand-transition :group="true">
          <template v-if="showMore">
            <v-col cols="6" sm="3">
              <GuessMetadataHeader
                v-if="rowGuessMeta(['middleName', 'suffix', 'status'])"
                :metadata="guessMetaData('middleName')"
                label="Middle Name"
                @highlight-bounding-box="onHighlightBoundingBox" />
              <v-text-field
                type="text"
                :model-value="patientValue?.middleName"
                :disabled="!props.editable"
                label="Middle Name"
                @update:model-value="(v) => onChangeInfo('middleName', v)" />
            </v-col>
            <v-col cols="6" sm="3">
              <GuessMetadataHeader
                v-if="rowGuessMeta(['middleName', 'suffix', 'status'])"
                :metadata="guessMetaData('suffix')"
                label="Suffix"
                @highlight-bounding-box="onHighlightBoundingBox" />
              <v-text-field
                type="text"
                :model-value="patientValue?.suffix"
                :disabled="!props.editable"
                label="Suffix"
                @update:model-value="(v) => onChangeInfo('suffix', v)" />
            </v-col>
            <v-col
              v-if="
                requirementsStore.clientConfig?.intake?.extraFields?.includes('patient.status') &&
                patientValue?.extraData?.status
              "
              cols="6"
              sm="3">
              <GuessMetadataHeader
                v-if="rowGuessMeta(['middleName', 'suffix', 'status'])"
                :metadata="guessMetaData('status')"
                label="Status"
                @highlight-bounding-box="onHighlightBoundingBox" />
              <v-text-field
                type="text"
                :model-value="patientValue?.extraData?.status"
                :disabled="true"
                label="Status" />
            </v-col>
            <v-col
              v-if="
                requirementsStore.clientConfig?.intake?.extraFields?.includes('patient.externalId') && !addPatientState
              "
              cols="6"
              sm="3">
              <GuessMetadataHeader v-if="rowGuessMeta(['middleName', 'suffix', 'status'])" />
              <v-text-field
                type="text"
                :model-value="patientValue?.externalId"
                :disabled="patient?.externalId !== ''"
                label="Patient ID (MRN)"
                @update:model-value="(v) => onChangeInfo('externalId', v)"></v-text-field>
            </v-col>
          </template>
        </v-expand-transition>
        <v-col v-if="!addPatientState" cols="12" class="patient-bar__actions">
          <v-btn
            variant="tonal"
            color="primary"
            rounded
            class="mt-3"
            :append-icon="showMore ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            @click="toggleShowMore">
            {{ showMore ? "Show less details" : "Show more details" }}
          </v-btn>
          <v-btn
            v-if="props.editable"
            variant="tonal"
            color="primary"
            rounded
            class="mt-3 ml-2"
            prepend-icon="mdi-account-switch-outline"
            @click="changePatient">
            Change Patient
          </v-btn>
          <v-btn
            v-if="props.editable && hasPatientChanges && !hasPendingPatientSelection"
            class="mt-3 ml-2"
            color="warning"
            variant="flat"
            rounded
            @click="requestUpdatePatient">
            Update Patient
          </v-btn>
        </v-col>
        <v-col v-if="props.editable && hasPatientChanges && !hasPendingPatientSelection" cols="12">
          <span class="text-warning">
            <v-icon>mdi-information-outline</v-icon> [Warning] Click "Update Patient" to overwrite patient information.
          </span>
        </v-col>
      </v-row>
      <v-dialog v-model="confirmOverwriteDialog" max-width="480">
        <v-card>
          <v-card-title class="text-h6">Confirm Patient Update</v-card-title>
          <v-card-text>
            Are you sure you want to overwrite existing patient information?
            <div v-if="!patient?.externalId && editablePatient?.externalId" class="text-warning mt-2">
              <v-icon>mdi-alert</v-icon> Warning: If an incorrect Patient ID (MRN) is entered, wrong upstream EHR
              patient record might be impacted.
              <div v-if="duplicateExternalIdCount > 0" class="text-error mt-2">
                <v-icon>mdi-alert-circle</v-icon> {{ duplicateExternalIdCount }} Patient(s) found on DB with same
                Patient ID (MRN), can't perform update
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn-lh variant="text" @click="confirmOverwriteDialog = false">Cancel</v-btn-lh>
            <v-btn-lh color="warning" :disabled="duplicateExternalIdCount > 0" @click="confirmAndUpdatePatient">
              Confirm
            </v-btn-lh>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-row v-if="addPatientState">
        <v-col cols="12">
          <span class="text-primary">
            <v-icon>mdi-information-outline</v-icon> Click "Save New Patient" to create a new patient.
          </span>
        </v-col>
        <v-col cols="auto">
          <v-btn-lh color="warning" @click="saveNewPatient">Save New Patient</v-btn-lh>
        </v-col>
        <v-col cols="auto">
          <v-btn-lh @click="setAddPatientState">Cancel</v-btn-lh>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script setup>
import { Patient } from "@/models/intake";
import GuessMetadataHeader from "@/components/intakeReview/GuessMetadataHeader.vue";
import { onMounted, ref, watch, computed } from "vue";
import { patientService } from "@/services/patientService";
import { useDate } from "vuetify";
import { requiredRule, lengthRule } from "@/utils/form";
import { getPatientNameRulesForClient } from "@/clientSpecific/base/intakeUI";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { convertDateToMMDDYYYY, formatPatientAddress } from "@/utils";
import { PATIENT_IDENTITY_ROW_FIELDS, useGuessMetadata } from "@/composables/useGuessMetadata";

const dateAdapter = useDate();
const requirementsStore = useRequirementsStore();
const emit = defineEmits(["change-attribute", "set-patient", "save-new-patient", "highlight-bounding-box"]);
const props = defineProps({
  patient: {
    required: false,
    type: Patient,
    default: null,
  },
  patientGuess: {
    required: false,
    type: Object,
    default: null,
  },
  clientCompany: {
    required: true,
    type: Number,
  },
  editable: {
    required: false,
    type: Boolean,
    default: false,
  },
});
const addPatientState = ref(false);
const showMore = ref(false);
const isChangingPatient = ref(false);
const hasPendingPatientSelection = ref(false);
const patient = computed(() => props.patient);
const newPatient = ref(null); // This is used to store the new patient when adding a new patient
const patientFormRef = ref(null);
const editablePatient = ref(null);
const searchablePatient = ref(new Patient({}, dateAdapter));
const patientNameRules = ref([requiredRule]);
const editableFields = ["firstName", "lastName", "phoneNumber", "dob"];

const isNewPatient = computed(() => !(newPatient.value ?? patient.value)?.externalId);

watch(
  [() => requirementsStore.clientConfig, isNewPatient],
  async ([config, newPatientFlag]) => {
    patientNameRules.value = config ? await getPatientNameRulesForClient(config, newPatientFlag) : [requiredRule];
  },
  { immediate: true }
);

const patientDobRequired = computed(() => {
  return requirementsStore.clientConfig?.intake?.requiredFields?.includes("patientDob") || false;
});

const patientPhoneRequired = computed(() => {
  return requirementsStore.clientConfig?.intake?.requiredFields?.includes("patientPhone") || false;
});

const patientAddress = computed(() => formatPatientAddress(newPatient.value ?? editablePatient.value ?? patient.value));

const { guessMetaData, rowGuessMeta } = useGuessMetadata(() => props.patientGuess);

const onHighlightBoundingBox = (metadata) => emit("highlight-bounding-box", metadata);

const _phoneNumberLengthRuleFn = lengthRule(10, "Phone number must be 10 digits");
const patientPhoneLenghtRule = (value) => _phoneNumberLengthRuleFn(value?.replace(/\D/g, ""));

const getChangedFields = () => {
  if (!editablePatient.value || !patient.value) {
    return [];
  }

  return editableFields.filter((fieldName) => {
    const newValue = editablePatient.value[fieldName];
    const originalValue = patient.value[fieldName];
    return newValue !== originalValue;
  });
};

const hasPatientChanges = computed(() => {
  return getChangedFields().length > 0;
});

const updatePatient = () => {
  getChangedFields().forEach((fieldName) => {
    const newValue = editablePatient.value[fieldName];
    emit("change-attribute", fieldName, newValue);
  });
};

const confirmOverwriteDialog = ref(false);
const selectPatientDialog = ref(false);
const searchResults = ref([]);
const searchLoading = ref(false);
const duplicateExternalIdCount = ref(0);
let externalIdCheckTimeout = null;

const requestUpdatePatient = async () => {
  if (!patientFormRef.value) return;

  const validation = await patientFormRef.value.validate();
  if (!validation.valid) {
    return;
  }

  confirmOverwriteDialog.value = true;
};
const confirmAndUpdatePatient = () => {
  updatePatient();
  confirmOverwriteDialog.value = false;
};

watch(addPatientState, (newState) => {
  if (newState) {
    const initialData = { ...searchablePatient.value };
    const requiredFields = requirementsStore.clientConfig?.intake?.requiredFields || [];

    // Handle any empty values generically based on client requirements
    Object.keys(initialData).forEach((fieldName) => {
      if (!initialData[fieldName] || initialData[fieldName] === "") {
        const isFieldRequired = requiredFields.includes(fieldName);
        if (isFieldRequired) {
          // Set to null if required but no guess provided
          initialData[fieldName] = null;
        } else {
          // Remove property entirely if not required
          delete initialData[fieldName];
        }
      }
    });

    newPatient.value = initialData;
    newPatient.value.address = null;
    newPatient.value.externalId = null;
    showMore.value = true;
    isChangingPatient.value = false;
  } else {
    newPatient.value = null;
    isChangingPatient.value = props.patient === null;
  }
});

watch(isChangingPatient, (newState) => {
  if (newState) {
    Object.assign(searchablePatient.value, { ...editablePatient.value });
  }
});

watch(patient, (newValue, oldValue) => {
  // anytime the patient changes, we need to reset the dropdown state
  if (newValue?.id !== oldValue?.id) {
    isChangingPatient.value = false;
  }

  if (newValue) {
    editablePatient.value = { ...newValue };
    hasPendingPatientSelection.value = false;
  }
});

const checkDuplicateExternalId = async (externalId) => {
  if (!externalId || externalId === patient.value?.externalId) {
    duplicateExternalIdCount.value = 0;
    return;
  }
  try {
    const response = await patientService.getByExternalId(externalId);
    duplicateExternalIdCount.value = response?.count || 0;
  } catch {
    duplicateExternalIdCount.value = 0;
  }
};

watch(
  () => editablePatient.value?.externalId,
  (newExternalId) => {
    if (externalIdCheckTimeout) {
      clearTimeout(externalIdCheckTimeout);
    }
    externalIdCheckTimeout = setTimeout(() => {
      checkDuplicateExternalId(newExternalId);
    }, 500);
  }
);

const patientRequiredRule = (value) => requiredRule(value) === true || "Please select a patient";

const setAddPatientState = () => {
  addPatientState.value = !addPatientState.value;
};

const searchPatient = async () => {
  searchLoading.value = true;
  selectPatientDialog.value = true;
  try {
    const response = await patientService.search(
      {
        firstName: searchablePatient.value.firstName,
        lastName: searchablePatient.value.lastName,
        phoneNumber: searchablePatient.value.phoneNumber,
        dob: searchablePatient.value.dob,
        address: searchablePatient.value.address,
        externalId: searchablePatient.value.externalId,
      },
      props.clientCompany
    );
    const results = response?.results || [];
    searchResults.value = results.map((p) => ({
      id: p.id,
      firstName: p.firstName,
      lastName: p.lastName,
      phoneNumber: p.phoneNumber,
      dob: p.dob,
      address: p.address,
      externalId: p.externalId,
      patient: p,
    }));
  } catch (error) {
    searchResults.value = [];
  } finally {
    searchLoading.value = false;
  }
};

onMounted(() => {
  if (!props.patient) {
    isChangingPatient.value = true;
    if (props.patientGuess) {
      Object.assign(searchablePatient.value, new Patient({ ...props.patientGuess }, dateAdapter));
    }
  } else {
    editablePatient.value = { ...props.patient };
  }
});

const toggleShowMore = () => {
  showMore.value = !showMore.value;
};

const onSelectPatient = (id) => {
  if (!id) return;
  hasPendingPatientSelection.value = true;
  isChangingPatient.value = false;
  addPatientState.value = false;
  const item = searchResults.value.find((p) => p.id === id);
  if (item) {
    editablePatient.value = new Patient(item.patient, dateAdapter);
    emit("set-patient", item.patient);
    selectPatientDialog.value = false;
  }
};

const onChangeInfo = (name, value) => {
  if (!addPatientState.value) {
    editablePatient.value[name] = value;
  } else {
    newPatient.value[name] = value;
  }
};

const saveNewPatient = async () => {
  if (!patientFormRef.value) return;

  const validation = await patientFormRef.value.validate();
  if (!validation.valid) {
    return;
  }

  emit("save-new-patient", { ...newPatient.value });
  newPatient.value = null;
  addPatientState.value = false;
};

const changePatient = () => {
  isChangingPatient.value = true;
};

const cancelChangePatient = () => {
  isChangingPatient.value = false;
};
</script>

<style lang="scss" scoped>
.patient-bar__label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: $lh_font_family_dm_sans;
  font-size: 12.5px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $lh_color_blue_gray;
  margin: 0 0 12px;
}

.patient-bar__actions {
  .v-btn {
    letter-spacing: normal;
    text-transform: none;
    font-weight: 500;
  }
}
</style>
